/**
 * Pure cache-mutation helpers shared by two callers:
 *   - `./use-trading-actions.ts` — `onMutate` / `onSuccess` / `onError` of mutations
 *   - `./use-hyper-user-events.ts` — WS push handlers
 *
 * Design invariants:
 *   - Placeholder orders carry a **negative oid** so they are distinguishable
 *     from real server-assigned ids (`isOptimisticOrder`). `onSuccess` (or the
 *     WS "open" event, whichever arrives first) swaps it for the real oid.
 *   - `reduceAccountPositions` shrinks position magnitude while preserving
 *     sign; zero positions are removed to match server behavior on close.
 *   - `upsertOrdersFromWs` matches on oid first, then falls back to
 *     placeholder match on (coin, side, limitPx, origSz). If neither matches,
 *     it returns `"unknown"` — the WS event shape is minimal and cannot
 *     reconstruct fields like `triggerCondition`, `orderType`, `tif`, so the
 *     caller invalidates the openOrders query to refetch authoritative data.
 */
import type { QueryClient } from "@tanstack/react-query";
import {
	isTriggerOrderType,
	type PlaceOrderParams,
} from "#/features/trade/lib/order-execution";
import { toNumber } from "#/lib/formatters";
import { queryKeys } from "./query-keys";
import type { AccountState, OpenOrder } from "./use-hyperliquid-account";

/**
 * Optimistic placeholder orders use negative oids so the UI (and future
 * mutations) can distinguish them from real server-assigned ids.
 */
let optimisticOrderCounter = 0;
export function nextOptimisticOid(): number {
	return -++optimisticOrderCounter;
}

export function isOptimisticOrder(order: { oid: number }): boolean {
	return order.oid < 0;
}

function mapOrderType(orderType: string): OpenOrder["orderType"] {
	switch (orderType) {
		case "Market order":
			return "Market";
		case "Limit order":
			return "Limit";
		case "Stop limit":
			return "Stop Limit";
		case "Stop market":
			return "Stop Market";
		case "Take profit limit":
			return "Take Profit Limit";
		case "Take profit market":
			return "Take Profit Market";
		default:
			return "Limit";
	}
}

function mapTif(tif: string, orderType: string): OpenOrder["tif"] {
	if (orderType === "Market order") return "FrontendMarket";
	if (tif === "IOC") return "Ioc";
	if (tif === "FrontendMarket") return "FrontendMarket";
	return "Gtc";
}

export function buildPlaceholderOrder(params: PlaceOrderParams): OpenOrder {
	const isTrigger = isTriggerOrderType(params.orderType);
	return {
		coin: params.coin,
		side: params.side === "buy" ? "B" : "A",
		limitPx: params.price,
		sz: params.size,
		origSz: params.size,
		oid: nextOptimisticOid(),
		timestamp: Date.now(),
		isTrigger,
		triggerPx: isTrigger ? params.triggerPrice : "0",
		triggerCondition: "N/A",
		children: [],
		isPositionTpsl: false,
		reduceOnly: params.reduceOnly,
		orderType: mapOrderType(params.orderType),
		tif: mapTif(params.timeInForce, params.orderType),
		cloid: null,
	};
}

// ── Open-orders cache helpers ──────────────────────────────────────

export type OrderKey = { coin: string; oid: number };

const orderKeyOf = (o: OrderKey) => `${o.coin}:${o.oid}`;

/** Remove matching orders from the open-orders cache; returns what was removed. */
export function removeOrdersFromCache(
	queryClient: QueryClient,
	address: string | null,
	targets: OrderKey[],
): OpenOrder[] {
	if (targets.length === 0) return [];
	const key = queryKeys.hyper.openOrders(address);
	const targetKeys = new Set(targets.map(orderKeyOf));
	const current = queryClient.getQueryData<OpenOrder[]>(key) ?? [];
	const removed = current.filter((o) => targetKeys.has(orderKeyOf(o)));
	queryClient.setQueryData<OpenOrder[]>(key, (old) =>
		(old ?? []).filter((o) => !targetKeys.has(orderKeyOf(o))),
	);
	return removed;
}

export function restoreOrdersInCache(
	queryClient: QueryClient,
	address: string | null,
	orders: OpenOrder[],
) {
	if (orders.length === 0) return;
	const key = queryKeys.hyper.openOrders(address);
	queryClient.setQueryData<OpenOrder[]>(key, (old) => [
		...orders,
		...(old ?? []),
	]);
}

export function insertOrderIntoCache(
	queryClient: QueryClient,
	address: string | null,
	order: OpenOrder,
) {
	const key = queryKeys.hyper.openOrders(address);
	queryClient.setQueryData<OpenOrder[]>(key, (old) => [order, ...(old ?? [])]);
}

/**
 * Result of an incoming WS open-order upsert. "matched" means we already have
 * an entry (either a real oid or an optimistic placeholder we could swap);
 * "unknown" means we don't have enough fields to reconstruct the full row and
 * the caller should invalidate the openOrders query instead.
 */
export type UpsertOutcome =
	| { kind: "matched"; coin: string; oid: number }
	| { kind: "unknown"; coin: string; oid: number };

/** Minimal shape the WS orderUpdates event gives us for an open/triggered order. */
export type WsOrderPatch = {
	coin: string;
	side: "B" | "A";
	limitPx: string;
	sz: string;
	origSz: string;
	oid: number;
	timestamp: number;
	reduceOnly?: boolean;
	/** Only the modify mutation passes this — WS orderUpdates carries no trigger info. */
	triggerPx?: string;
};

/**
 * Upsert WS-reported open orders into the cache. Tries three things in order:
 *   1. oid already present → merge WS fields onto existing row
 *   2. optimistic placeholder (negative oid) with matching coin/side/limitPx/origSz
 *      → replace its oid with the real one
 *   3. neither → return "unknown" so caller can invalidate for a full refetch
 *      (we can't fabricate triggerCondition/orderType/tif without guessing)
 */
export function upsertOrdersFromWs(
	queryClient: QueryClient,
	address: string | null,
	patches: WsOrderPatch[],
): UpsertOutcome[] {
	if (patches.length === 0) return [];
	const key = queryKeys.hyper.openOrders(address);
	const outcomes: UpsertOutcome[] = [];

	queryClient.setQueryData<OpenOrder[]>(key, (old) => {
		const current = old ?? [];
		const byKey = new Map<string, OpenOrder>(
			current.map((o) => [`${o.coin}:${o.oid}`, o]),
		);

		for (const patch of patches) {
			const patchKey = `${patch.coin}:${patch.oid}`;
			const existing = byKey.get(patchKey);
			if (existing) {
				byKey.set(patchKey, {
					...existing,
					side: patch.side,
					limitPx: patch.limitPx,
					sz: patch.sz,
					origSz: patch.origSz,
					timestamp: patch.timestamp,
					reduceOnly: patch.reduceOnly ?? existing.reduceOnly,
					triggerPx: patch.triggerPx ?? existing.triggerPx,
				});
				outcomes.push({ kind: "matched", coin: patch.coin, oid: patch.oid });
				continue;
			}

			const placeholder = current.find(
				(o) =>
					o.oid < 0 &&
					o.coin === patch.coin &&
					o.side === patch.side &&
					o.limitPx === patch.limitPx &&
					o.origSz === patch.origSz,
			);
			if (placeholder) {
				byKey.delete(`${placeholder.coin}:${placeholder.oid}`);
				byKey.set(patchKey, {
					...placeholder,
					oid: patch.oid,
					sz: patch.sz,
					timestamp: patch.timestamp,
					reduceOnly: patch.reduceOnly ?? placeholder.reduceOnly,
				});
				outcomes.push({ kind: "matched", coin: patch.coin, oid: patch.oid });
				continue;
			}

			outcomes.push({ kind: "unknown", coin: patch.coin, oid: patch.oid });
		}

		return Array.from(byKey.values());
	});

	return outcomes;
}

/** Upgrade a placeholder order to the real server-assigned oid, preserving other fields. */
export function replaceOrderOidInCache(
	queryClient: QueryClient,
	address: string | null,
	coin: string,
	fromOid: number,
	toOid: number,
) {
	const key = queryKeys.hyper.openOrders(address);
	queryClient.setQueryData<OpenOrder[]>(key, (old) =>
		(old ?? []).map((o) =>
			o.coin === coin && o.oid === fromOid ? { ...o, oid: toOid } : o,
		),
	);
}

// ── Position cache helpers ─────────────────────────────────────────

export type PositionReduction = { coin: string; deltaAbs: number };
export type PositionSnapshot = Map<
	string,
	AccountState["assetPositions"][number]
>;

/**
 * Reduce the magnitude of each matching position's `szi` by `deltaAbs` while
 * preserving sign. Positions that reach 0 are removed (mirrors what the
 * server returns for a fully closed position).
 *
 * Returns a snapshot keyed by coin so the caller can roll back on error.
 */
export function reduceAccountPositions(
	queryClient: QueryClient,
	address: string | null,
	reductions: PositionReduction[],
): PositionSnapshot {
	const key = queryKeys.hyper.account(address);
	const snapshot: PositionSnapshot = new Map();
	if (reductions.length === 0) return snapshot;

	const byCoin = new Map(reductions.map((r) => [r.coin, r.deltaAbs]));
	const current = queryClient.getQueryData<AccountState>(key);
	if (!current) return snapshot;

	for (const entry of current.assetPositions) {
		if (byCoin.has(entry.position.coin)) {
			snapshot.set(entry.position.coin, entry);
		}
	}

	queryClient.setQueryData<AccountState>(key, (old) => {
		if (!old) return old;
		return {
			...old,
			assetPositions: old.assetPositions.flatMap((entry) => {
				const delta = byCoin.get(entry.position.coin);
				if (delta == null) return [entry];
				const signed = toNumber(entry.position.szi);
				const sign = signed >= 0 ? 1 : -1;
				const newAbs = Math.max(0, Math.abs(signed) - Math.abs(delta));
				if (newAbs === 0) return [];
				return [
					{
						...entry,
						position: {
							...entry.position,
							szi: String(sign * newAbs),
						},
					},
				];
			}),
		};
	});

	return snapshot;
}

export function restoreAccountPositions(
	queryClient: QueryClient,
	address: string | null,
	snapshot: PositionSnapshot,
) {
	if (snapshot.size === 0) return;
	const key = queryKeys.hyper.account(address);
	queryClient.setQueryData<AccountState>(key, (old) => {
		if (!old) return old;
		const snapshotCoins = new Set(snapshot.keys());
		const preserved = old.assetPositions.filter(
			(entry) => !snapshotCoins.has(entry.position.coin),
		);
		return {
			...old,
			assetPositions: [...preserved, ...snapshot.values()],
		};
	});
}

// ── Result inspection ──────────────────────────────────────────────

type PlaceOrderResult = {
	response?: { data?: { statuses?: unknown[] } };
};

export function extractRestingOid(result: unknown): number | null {
	if (!result || typeof result !== "object") return null;
	const status = (result as PlaceOrderResult).response?.data?.statuses?.[0];
	if (!status || typeof status !== "object") return null;
	const resting = (status as { resting?: { oid?: unknown } }).resting;
	if (!resting || typeof resting !== "object") return null;
	const oid = (resting as { oid?: unknown }).oid;
	return typeof oid === "number" ? oid : null;
}
